import { useState, useEffect } from "react";
import axios from "axios";
import { Sidebar } from "@/pages/AdminDashboard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAdminAuth, getAdminAuthHeaders } from "@/hooks/useAdminAuth";
import { Loader2, KeyRound, CheckCircle2, AlertTriangle, RefreshCw, ShieldCheck, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PlatformSettings() {
  const { isLoading: authLoading, isAuthenticated, logout } = useAdminAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ secret_key: "", publishable_key: "", webhook_secret: "" });
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/platform/stripe`, { headers: getAdminAuthHeaders() });
      setSettings(res.data);
    } catch (e) {
      toast.error("Failed to load Stripe settings");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isAuthenticated) fetchSettings();
  }, [isAuthenticated]);

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/admin/platform/stripe/test`,
        { secret_key: form.secret_key || undefined },
        { headers: getAdminAuthHeaders() }
      );
      setTestResult(res.data);
      if (res.data.success) toast.success(`Stripe key valid (${res.data.mode} mode)`);
      else toast.error(`Test failed: ${res.data.message}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    }
    setTesting(false);
  };

  const handleSave = async () => {
    if (!form.secret_key && !form.publishable_key && !form.webhook_secret) {
      toast.error("Enter at least one key to update");
      return;
    }
    if (!window.confirm("This will replace the active Stripe keys for ALL checkouts immediately. Continue?")) return;
    setSaving(true);
    try {
      const payload = {};
      if (form.secret_key) payload.secret_key = form.secret_key;
      if (form.publishable_key) payload.publishable_key = form.publishable_key;
      if (form.webhook_secret) payload.webhook_secret = form.webhook_secret;

      const res = await axios.post(`${API}/admin/platform/stripe`, payload, { headers: getAdminAuthHeaders() });
      toast.success(res.data.message);
      setForm({ secret_key: "", publishable_key: "", webhook_secret: "" });
      setTestResult(null);
      await fetchSettings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const handleRemoveOverride = async () => {
    if (!window.confirm("Remove DB override and fall back to environment variables?")) return;
    try {
      await axios.delete(`${API}/admin/platform/stripe`, { headers: getAdminAuthHeaders() });
      toast.success("Override removed. Using environment vars.");
      await fetchSettings();
    } catch (e) {
      toast.error("Failed to remove override");
    }
  };

  if (authLoading || loading) {
    return <div className="flex items-center justify-center min-h-screen"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar onLogout={logout} />
      <main className="ml-64 p-8" data-testid="platform-settings-page">
        <div className="max-w-3xl">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
              <KeyRound className="w-7 h-7 text-bronze" /> Platform Settings
            </h1>
            <p className="text-slate-500 mt-1">Manage Stripe API keys used for plugin sales checkout.</p>
          </div>

          {/* Current Active Keys */}
          <Card className="mb-6" data-testid="current-stripe-keys">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Current Active Keys</span>
                <div className="flex items-center gap-2">
                  {settings?.mode === "live" && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">LIVE</Badge>}
                  {settings?.mode === "test" && <Badge className="bg-amber-100 text-amber-800 border-amber-200">TEST</Badge>}
                  <Badge className="bg-slate-100 text-slate-700 border-slate-200 uppercase text-xs">{settings?.source || "environment"}</Badge>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="text-slate-500 mb-1">Secret Key</div>
                  <div className="font-mono text-slate-800" data-testid="active-secret-key">{settings?.secret_key_masked || "—"}</div>
                </div>
                <div>
                  <div className="text-slate-500 mb-1">Publishable Key</div>
                  <div className="font-mono text-slate-800 truncate" data-testid="active-publishable-key">{settings?.publishable_key || "—"}</div>
                </div>
                <div>
                  <div className="text-slate-500 mb-1">Webhook Secret</div>
                  <div className="font-mono text-slate-800" data-testid="active-webhook-secret">{settings?.webhook_secret_masked || "—"}</div>
                </div>
              </div>
              {settings?.updated_at && (
                <div className="text-xs text-slate-500 pt-2 border-t border-slate-100">
                  Last updated by <strong>{settings.updated_by || "system"}</strong> on {new Date(settings.updated_at).toLocaleString()}
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={fetchSettings} data-testid="refresh-btn">
                  <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                </Button>
                <Button variant="outline" size="sm" onClick={handleTest} disabled={testing} data-testid="test-active-btn">
                  {testing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-1" />}
                  Test Current Key
                </Button>
                {settings?.has_db_override && (
                  <Button variant="outline" size="sm" onClick={handleRemoveOverride} className="text-red-600 hover:text-red-700" data-testid="remove-override-btn">
                    <Trash2 className="w-4 h-4 mr-1" /> Remove DB Override
                  </Button>
                )}
              </div>
              {testResult && (
                <div className={`mt-3 rounded-lg p-3 text-sm ${testResult.success ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-red-50 text-red-800 border border-red-200"}`}>
                  {testResult.success ? <CheckCircle2 className="inline w-4 h-4 mr-1" /> : <AlertTriangle className="inline w-4 h-4 mr-1" />}
                  <span data-testid="test-result-msg">{testResult.message}</span>
                  {testResult.key_prefix && <span className="ml-2 font-mono text-xs opacity-75">({testResult.key_prefix})</span>}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Update Keys */}
          <Card data-testid="update-stripe-keys">
            <CardHeader>
              <CardTitle>Update Stripe Keys</CardTitle>
              <p className="text-sm text-slate-500">Get keys from your <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" className="text-bronze underline">Stripe Dashboard → Developers → API keys</a>. Changes apply instantly to new checkouts.</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm font-semibold">Secret Key</Label>
                <Input
                  type="password"
                  placeholder="sk_live_... or sk_test_..."
                  className="mt-1 font-mono"
                  value={form.secret_key}
                  onChange={(e) => setForm(f => ({ ...f, secret_key: e.target.value }))}
                  data-testid="new-secret-key-input"
                />
                <p className="text-xs text-slate-400 mt-1">Never share this. Used for server-side Stripe API calls.</p>
              </div>
              <div>
                <Label className="text-sm font-semibold">Publishable Key (optional)</Label>
                <Input
                  placeholder="pk_live_... or pk_test_..."
                  className="mt-1 font-mono"
                  value={form.publishable_key}
                  onChange={(e) => setForm(f => ({ ...f, publishable_key: e.target.value }))}
                  data-testid="new-publishable-key-input"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold">Webhook Signing Secret (optional)</Label>
                <Input
                  type="password"
                  placeholder="whsec_..."
                  className="mt-1 font-mono"
                  value={form.webhook_secret}
                  onChange={(e) => setForm(f => ({ ...f, webhook_secret: e.target.value }))}
                  data-testid="new-webhook-secret-input"
                />
                <p className="text-xs text-slate-400 mt-1">From Stripe Dashboard → Developers → Webhooks → your endpoint.</p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
                <AlertTriangle className="inline w-4 h-4 mr-1" />
                <strong>Important:</strong> The system will verify your Secret Key with Stripe's API before saving. Invalid keys are rejected.
              </div>

              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={handleTest} disabled={testing || !form.secret_key} data-testid="test-new-key-btn">
                  {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                  Test Key
                </Button>
                <Button onClick={handleSave} disabled={saving} className="bg-bronze hover:bg-amber-600" data-testid="save-stripe-keys-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  Save & Activate
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="mt-6 bg-slate-100 rounded-lg p-4 text-xs text-slate-600">
            <p className="font-semibold text-slate-800 mb-1">How this works</p>
            <p>Keys entered here are stored in the database and take precedence over the <code className="text-bronze">STRIPE_API_KEY</code> environment variable. This means you can rotate your Stripe keys without redeploying the app. If the DB entry is removed, the app falls back to the env var.</p>
          </div>
        </div>
      </main>
    </div>
  );
}
